import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Filter, MapPin, Sparkles, MessageSquare, Clock, Lock, Unlock, Phone, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { unlockRequirementPayment } from "../lib/payments";
import { CATEGORIES, categoryById } from "../lib/data";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import RequirementsForm from "../components/RequirementsForm";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

export default function Requirements() {
  const { user, patchUser } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [unlockingId, setUnlockingId] = useState("");
  const isAdmin = user && user.role === "admin";

  const load = () => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    if (q) params.q = q;
    api.get("/requirements", { params })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const counts = useMemo(() => {
    const map = {};
    for (const it of items) map[it.category] = (map[it.category] || 0) + 1;
    return map;
  }, [items]);

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title="Buyer Requirements | The Pataudi" description="Browse what buyers are looking for and post your own requirement — free." />
      <Header />
      <main className="flex-1 bg-slate-50/40">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold uppercase tracking-wider">
                <Sparkles className="h-3 w-3" /> Requirements Board
              </span>
              <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl text-ink">What buyers are looking for</h1>
              <p className="text-muted-foreground text-sm mt-1 max-w-xl">Post a requirement in 30 seconds — sellers matching your need will reach out. Or browse open requirements below to find your next customer.</p>
            </div>
            <button onClick={() => setShowForm((s) => !s)} className="px-4 h-10 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90" data-testid="toggle-req-form">
              {showForm ? "Hide form" : "Post Requirement"}
            </button>
          </div>

          {showForm && (
            <div className="mt-5 bg-white border border-border rounded-2xl p-5 shadow-soft" data-testid="req-form-wrapper">
              <RequirementsForm />
            </div>
          )}

          {/* Filter chips */}
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground font-semibold uppercase tracking-wider"><Filter className="h-3 w-3" /> Filter</span>
            <button onClick={() => setCategory("all")} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${category === "all" ? "bg-ink text-white border-ink" : "bg-white border-neutral-200 text-ink hover:border-neutral-400"}`} data-testid="req-filter-all">
              All
            </button>
            {CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setCategory(c.id)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${category === c.id ? "bg-ink text-white border-ink" : "bg-white border-neutral-200 text-ink hover:border-neutral-400"}`} data-testid={`req-filter-${c.id}`}>
                {c.label}{counts[c.id] ? ` · ${counts[c.id]}` : ""}
              </button>
            ))}
          </div>

          <div className="mt-4">
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} placeholder="Search descriptions or city…" className="w-full md:w-96 h-10 px-3 rounded-lg border border-neutral-200 bg-white text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none" data-testid="req-search" />
          </div>

          {/* List */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="req-list">
            {loading ? (
              [0, 1, 2].map((i) => <div key={i} className="rounded-2xl border border-neutral-200 bg-white h-40 animate-pulse" />)
            ) : items.length === 0 ? (
              <div className="col-span-full bg-white rounded-2xl border border-dashed border-neutral-300 p-10 text-center">
                <MessageSquare className="h-8 w-8 text-neutral-400 mx-auto mb-2" />
                <p className="font-semibold text-ink">No requirements yet</p>
                <p className="text-sm text-muted-foreground mt-1">Be the first to post one — it takes 30 seconds.</p>
                <button onClick={() => setShowForm(true)} className="mt-4 px-4 h-9 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90">Post Requirement</button>
              </div>
            ) : items.map((r) => {
              const cat = categoryById(r.category);
              const isOwner = user && r.user_id === user.id;
              const canSeeFull = isAdmin || isOwner || r.phoneUnlocked;
              const hasPhone = !!r.phone;
              const doUnlock = async () => {
                if (!user) { toast.info("Please log in to unlock contact."); navigate("/login", { state: { from: "/requirements" } }); return; }
                setUnlockingId(r.id);
                try {
                  const data = await unlockRequirementPayment(r.id);
                  patchUser({ unlockedRequirements: data.unlockedRequirements });
                  setItems((prev) => prev.map((x) => x.id === r.id ? { ...x, phone: data.phone, phoneUnlocked: true } : x));
                  toast.success("Payment successful — contact unlocked. You can now call the buyer.");
                } catch (e) {
                  if (e?.message === "CANCELLED") {
                    toast.info("Payment cancelled.");
                  } else {
                    toast.error(e?.message || "Could not unlock contact.");
                  }
                } finally { setUnlockingId(""); }
              };
              return (
                <article key={r.id} className="bg-white rounded-2xl border border-neutral-200 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_28px_rgba(0,0,0,0.06)] transition flex flex-col" data-testid={`req-card-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap">{cat?.label || r.category}</span>
                    <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5 whitespace-nowrap"><Clock className="h-3 w-3" />{timeAgo(r.created_at)}</span>
                  </div>
                  <p className="mt-2 text-sm text-ink leading-relaxed line-clamp-4">{r.description}</p>
                  <div className="mt-3 flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                    {r.city && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{r.city}</span>}
                    {r.budget && <span className="font-semibold text-emerald-600">{r.budget}</span>}
                    {r.name && <span>· {r.name}</span>}
                  </div>
                  {/* Phone + unlock CTA — hidden if no phone was posted */}
                  {hasPhone && (
                    <div className="mt-3 border-t border-dashed border-neutral-200 pt-3 flex items-center justify-between gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1.5 text-sm font-mono ${canSeeFull ? "text-emerald-700 font-semibold" : "text-neutral-500"}`} data-testid={`req-phone-${r.id}`}>
                        <Phone className="h-3.5 w-3.5" /> {r.phone || "—"}
                      </span>
                      {canSeeFull ? (
                        r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 h-8 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap" data-testid={`req-call-${r.id}`}><Unlock className="h-3.5 w-3.5" /> Call</a>
                      ) : (
                        <button
                          onClick={doUnlock}
                          disabled={unlockingId === r.id}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-3 h-8 rounded-full text-white whitespace-nowrap disabled:opacity-70"
                          style={{ background: "linear-gradient(135deg,#14532D 0%,#166534 100%)" }}
                          data-testid={`req-unlock-${r.id}`}
                        >
                          {unlockingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Lock className="h-3.5 w-3.5" /> Unlock Contact – ₹199</>}
                        </button>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
